<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.companyList.modal.new.buttonText')"
      @click="companyModalOpen = true"
    />
    <export
      url="/companies/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.companies')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <company-modal v-model:open="companyModalOpen" v-model:company="editedCompany" @saved="refetch" />
  <data-table
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    class="mb-auto ml-12"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :actions="actions"
  />
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  ref, reactive, computed, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { enumOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';

import type { Filter } from '@utils/filter/Filter.vue';
import { useDelete } from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import CompanyModal from '@pages/companies/CompanyModal.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { useTableFields } from '@hooks/table-configuration';
import type { HydraCollection, Company } from '@/entities/Entities';
import { useQueryRef } from '@/hooks/url';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const companyModalOpen = ref(false);
const editedCompany = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const [deleteCompany, { loading: deleting }] = useDelete();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.companyList.actions.edit'),
    action: (item: Company) => {
      companyModalOpen.value = true;
      editedCompany.value = { ...item };
    },
  },
  {
    title: t('pages.companyList.actions.delete'),
    action: async (item: Company) => {
      if (await deleteCompany(`/companies/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.companyList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'name',
    title: t('company.name'),
    group: t('pages.companyList.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.companyList.filterGroups.general'),
      options: {
        label: t('company.name'),
      },
    },
  },
  {
    key: 'industry',
    title: t('company.industry'),
    group: t('pages.companyList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'dropdown',
      group: t('pages.companyList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/industry', (v) => ({
          value: v,
          label: v,
        })),
        label: t('company.industry'),
      },
    },
  },
  {
    key: 'size',
    title: t('company.size'),
    group: t('pages.companyList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'dropdown',
      group: t('pages.companyList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/CompanySize', (v) => ({
          value: v,
          label: v,
        })),
        label: t('company.size'),
      },
    },
  },
  {
    key: 'contactEmail',
    title: t('company.contactEmail'),
    group: t('pages.companyList.fieldGroups.contact'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.companyList.filterGroups.contact'),
      options: {
        label: t('company.contactEmail'),
      },
    },
  },
  {
    key: 'contactFirstName',
    title: t('company.contactFirstName'),
    group: t('pages.companyList.fieldGroups.contact'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.companyList.filterGroups.contact'),
      options: {
        label: t('company.contactFirstName'),
      },
    },
  },
  {
    key: 'contactLastName',
    title: t('company.contactLastName'),
    group: t('pages.companyList.fieldGroups.contact'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.companyList.filterGroups.contact'),
      options: {
        label: t('company.contactLastName'),
      },
    },
  },
  {
    key: 'contactTelephone',
    title: t('company.contactTelephone'),
    group: t('pages.companyList.fieldGroups.contact'),
    visible: true,
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.companyList.filterGroups.contact'),
      options: {
        label: t('company.contactTelephone'),
      },
    },
  },
  {
    key: 'contactLanguage',
    csvTranslations: {
      de: t('language.de'),
      en: t('language.en'),
      fr: t('language.fr'),
      it: t('language.it'),
    },
    title: t('company.contactLanguage'),
    group: t('pages.companyList.fieldGroups.contact'),
    sortable: true,
    formatter: (v: string|null) => (v ? t(`language.${v}`) : ''),
    filter: {
      type: 'dropdown',
      group: t('pages.companyList.filterGroups.contact'),
      options: {
        options: enumOptionsProvider('/enums/language', (v) => ({
          value: v,
          label: t(`language.${v}`),
        })),
        label: t('company.contactLanguage'),
      },
    },
  },
  {
    key: 'url',
    title: t('company.url'),
    group: t('pages.companyList.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.companyList.filterGroups.general'),
      options: {
        label: t('company.url'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<Company>>('/companies', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>

